import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  PapiBadge,
  PapiUser,
  PapiDeviation,
  PapiEditorText,
} from '@wix/da-types-papi';
import { isEditorTextEmpty } from '@wix/da-shared-react/pkg/DaEditorWrapper/utils';
import { useFlag } from '@wix/da-shared-react/pkg/redux/flags/hooks/useFlag';

import { formatDateOfBirth } from './utils';
import MutualConnections from './MutualConnections';
import ArtistInfoLine from './ArtistInfoLine';
import Badges from './Badges';
import UserInfoBullets from './UserInfoBullets';
import CollapsibleBio from './CollapsibleBio';
import SocialMediaList from './SocialMediaList';
import { AboutMeMode } from './types';

import s from './AboutMe.scss';

export interface Props {
  moduleId: number;
  moduleVersion: number;
  isEmpty?: boolean;
  user: PapiUser;
  gender?: string;
  isCurrentUserProfileOwner: boolean;
  isArtist?: boolean;
  isOpenForCommissions?: boolean;
  hasActiveCommissions?: boolean;
  level?: number;
  specialty?: number;
  website?: string;
  websiteLabel?: string;
  dobDay?: number;
  dobMonth?: number;
  dobYear?: number;
  countryId?: number;
  countrycode?: string;
  deviantFor: number; // duration in seconds
  devidDeviation?: PapiDeviation;
  bio?: PapiEditorText;
  badges?: PapiBadge[];
  showBadges: boolean;
  isMobile?: boolean;
  interests?: {
    id: number;
    value: string;
  }[];
  socialLinks?: {
    id: number;
    value?: string;
  }[];
  onStopEditing: () => void;
  hasJoinedWithEclipse?: boolean;
  mutualWatchedUsers?: PapiUser[];
  mutualWatchedUsersCount?: number;

  mode?: AboutMeMode;
}

const Gutter: React.FC<{
  cardMode: boolean;
}> = ({ cardMode, children }) => (
  <div className={classnames(cardMode && s['gutter'])}>{children}</div>
);
Gutter.displayName = 'Gutter';

const AboutMe: React.FC<Props> = ({
  socialLinks,
  moduleId,
  moduleVersion,
  dobYear,
  dobDay,
  dobMonth,
  countrycode,
  deviantFor,
  website,
  websiteLabel,
  level,
  specialty,
  gender,
  badges,
  showBadges,
  bio,
  isArtist,
  interests,
  user,
  mode = AboutMeMode.DEFAULT,
  isCurrentUserProfileOwner,
  mutualWatchedUsers = [],
  mutualWatchedUsersCount = 0,
}) => {
  const { t } = useTranslation();
  const mutualConnectionsEnabled = useFlag('mutual_connections');
  const isCard = mode === AboutMeMode.CARD;

  return (
    <div className={classnames(s['root'], isCard && s['card'])}>
      {mutualConnectionsEnabled && !isCurrentUserProfileOwner && isCard && (
        <Gutter cardMode={isCard}>
          <MutualConnections
            user={user}
            isWatch={user.isWatching || false}
            mutual={mutualWatchedUsers}
            mutualWatchedUsersCount={mutualWatchedUsersCount}
          />
        </Gutter>
      )}
      {isArtist && (
        <Gutter cardMode={isCard}>
          <ArtistInfoLine
            level={level}
            specialty={specialty}
            className={s['info-line']}
          />
        </Gutter>
      )}
      <Gutter cardMode={isCard}>
        <UserInfoBullets
          dateOfBirth={formatDateOfBirth(dobDay, dobMonth, dobYear, t)}
          countryCode={countrycode}
          deviantFor={deviantFor}
          gender={gender}
          website={website}
          websiteLabel={websiteLabel}
        />
      </Gutter>

      {socialLinks && socialLinks.length > 0 && (
        <Gutter cardMode={isCard}>
          <SectionAndLabel label={t('widgets.aboutMe.followMe')}>
            <SocialMediaList socialLinks={socialLinks} user={user} />
          </SectionAndLabel>
        </Gutter>
      )}
      {showBadges && badges && (
        <Gutter cardMode={isCard}>
          <SectionAndLabel label={t('widgets.aboutMe.myBadges')}>
            <Badges badges={badges} collapseMoreThan={6} />
          </SectionAndLabel>
        </Gutter>
      )}

      {bio && !isEditorTextEmpty(bio) && (
        <Gutter cardMode={isCard}>
          <SectionLabel>{t('widgets.aboutMe.edit.myBio')}</SectionLabel>
          <CollapsibleBio
            bio={bio}
            interests={interests}
            mode={mode}
            moduleId={moduleId}
            moduleVersion={moduleVersion}
          />
        </Gutter>
      )}
    </div>
  );
};
AboutMe.displayName = 'AboutMe';

// Ideally this would be a heading, but since we don't know what level heading we need, we should should hold off.
const SectionLabel = ({ children }) => (
  <div className={s['section-label']}>{children}</div>
);

const SectionAndLabel = ({ label, children }) => (
  <div className={s['section']}>
    <SectionLabel>{label}</SectionLabel>
    {children}
  </div>
);

export default AboutMe;
